import { defineStore } from 'pinia';
import { computed, createError, ref, useOrganizationDomain } from '#imports';
import {
  apiGetCurrentOrganization,
  getCurrentOrganizationOnServer,
  OrganizationDto,
} from '~/api/organizations';
import { getErrorStatus, ORGANIZATION_UNAVAILABLE_STATUS } from '~/api';

export const useOrganizationStore = defineStore('organization', () => {
  const organization = ref<OrganizationDto | null>(null);

  const nonNullableOrganization = computed<OrganizationDto>(() => {
    if (organization.value === null) {
      throw new Error('Organization must not be null');
    }
    return organization.value;
  });

  const isPublic = computed(() => organization.value?.isPublic ?? false);
  const displaySignIn = computed(
    () => organization.value?.displaySignInButton ?? true,
  );
  const currentOrganizationId = computed(() => organization.value?.id);
  const set = (currentOrganization: OrganizationDto) => {
    organization.value = currentOrganization;
  };

  const organizationDomain = useOrganizationDomain();

  const initializeOrganization = async (
    organizationSlug?: string,
  ): Promise<void> => {
    if (!organizationDomain && !organizationSlug) {
      organization.value = null;
      return;
    }
    if (
      (organizationSlug && organization.value?.slug !== organizationSlug) ||
      (organizationDomain &&
        organization.value?.customDomain !== organizationDomain)
    ) {
      try {
        organization.value = import.meta.server
          ? await getCurrentOrganizationOnServer({
              organizationSlug,
              organizationDomain,
            })
          : await apiGetCurrentOrganization({
              organizationSlug,
              organizationDomain,
            });
      } catch (err) {
        const status = getErrorStatus(err);
        if (status === ORGANIZATION_UNAVAILABLE_STATUS) {
          organization.value = null;
          throw createError({
            statusCode: ORGANIZATION_UNAVAILABLE_STATUS,
            fatal: true,
            cause: err,
          });
        }
        if (status !== 404) {
          throw err;
        }
      }
    }
  };

  return {
    organization,
    nonNullableOrganization,
    isPublic,
    displaySignIn,
    currentOrganizationId,
    set,
    initializeOrganization,
  };
});
